import { Button } from "@/components/ui/button";
import { Brain, Loader2, AlertCircle } from "lucide-react";
import { useComprehensiveNutrition } from "@/contexts/NutritionContext";
import { useProfile } from "@/hooks/useProfile";
import { useToast } from "@/hooks/use-toast";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

export const RecalculateGoalsButton = () => {
  const { t } = useTranslation();
  const { recalculateGoalsIfNeeded, isCalculating, nutritionGoals } = useComprehensiveNutrition();
  const { refetch: refetchProfile } = useProfile();
  const { toast } = useToast();
  const [isRecalculating, setIsRecalculating] = useState(false);
  const [showManualOverrideDialog, setShowManualOverrideDialog] = useState(false);

  const handleRecalculate = async () => {
    if (isCalculating || isRecalculating) {
      return; // Prevent multiple simultaneous calls
    }

    // Check if user has manual overrides - show blocking message instead of recalculating
    if (nutritionGoals?.is_manual_override) {
      setShowManualOverrideDialog(true);
      return;
    }

    await performRecalculation();
  };

  const performRecalculation = async () => {
    try {
      setIsRecalculating(true);
      // Refetch profile first to get the latest activity level and other data
      await refetchProfile();
      await recalculateGoalsIfNeeded(true); // Force recalculation
      toast({
        title: t('components.recalculateGoals.toast.successTitle'),
        description: t('components.recalculateGoals.toast.successDescription'),
      });
    } catch (error) {
      toast({
        title: t('components.recalculateGoals.toast.errorTitle'),
        description: t('components.recalculateGoals.toast.errorDescription'),
        variant: "destructive",
      });
    } finally {
      setIsRecalculating(false);
    }
  };

  const isDisabled = isCalculating || isRecalculating;

  return (
    <>
      <Button 
        onClick={handleRecalculate} 
        disabled={isDisabled}
        size="sm" 
        variant="outline"
        className="gap-2"
      >
        {isDisabled ? (
          <>
            <Loader2 className="h-4 w-4 animate-spin" />
            {t('components.recalculateGoals.recalculating')}
          </>
        ) : (
          <>
            <Brain className="h-4 w-4" />
            {t('components.recalculateGoals.recalculate')}
          </>
        )}
      </Button>

      <AlertDialog open={showManualOverrideDialog} onOpenChange={setShowManualOverrideDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertCircle className="h-5 w-5 text-destructive" />
              {t('components.recalculateGoals.manualGoalsTitle')}
            </AlertDialogTitle>
            <AlertDialogDescription className="space-y-2">
              <p>
                {t('components.recalculateGoals.manualGoalsDescription1')}
              </p>
              <p className="text-muted-foreground">
                {t('components.recalculateGoals.manualGoalsDescription2')}
              </p>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('components.recalculateGoals.gotIt')}</AlertDialogCancel>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};